import * as React from 'react';

import PropTypes from 'prop-types';
import { compose } from 'recompose';
import type { LegacyContextType } from 'types/legacy-context-types';
import connectToStores from 'vendor/cnpm/fluxible.v0-4/addons/connectToStores';

import {
  addPluginScope,
  removePluginScope,
  setSelectedPluginScope,
  updatePluginScope,
} from 'bundles/widget-simulator/actions/WidgetSimulatorActions';
import OptionsFormGroup from 'bundles/widget-simulator/components/tools/OptionsFormGroup';
import type WidgetSimulatorStoreClass from 'bundles/widget-simulator/stores/WidgetSimulatorStore';
import type { Option } from 'bundles/widget-simulator/types';

import _t from 'i18n!nls/widget-simulator';

type PropsFromCaller = {};

type PropsFromStores = {
  options: Array<Option>;
  selectedOption: Option | undefined;
};

type PropsToComponent = PropsFromCaller & PropsFromStores;

class PluginScopeEditor extends React.Component<PropsToComponent> {
  static contextTypes = {
    executeAction: PropTypes.func.isRequired,
  };

  declare context: LegacyContextType<typeof PluginScopeEditor.contextTypes>;

  handleAddButtonClick = () => {
    const { executeAction } = this.context;

    // TODO: Create a modal for this.
    // eslint-disable-next-line no-alert
    const label = prompt(_t('Enter a name for this scope:'));

    if (label) {
      executeAction(addPluginScope, { label });
    }
  };

  handleEditButtonClick = () => {
    const { executeAction } = this.context;
    const { selectedOption } = this.props;

    if (!selectedOption) {
      return;
    }

    // TODO: Create a modal for this.
    // eslint-disable-next-line no-alert
    const label = prompt(_t('Enter a new name for this scope:'));

    if (label) {
      executeAction(updatePluginScope, {
        id: selectedOption.id,
        newProps: { label },
      });
    }
  };

  handleRemoveButtonClick = () => {
    const { executeAction } = this.context;
    const { options, selectedOption } = this.props;

    if (!selectedOption) {
      return;
    }

    const selectedIndex = options.findIndex((option) => option.id === selectedOption.id);

    // TODO: Create a modal for this.
    // eslint-disable-next-line no-alert,
    if (confirm(_t('Are you sure you want to delete this scope? This cannot be undone.'))) {
      executeAction(removePluginScope, { index: selectedIndex });
    }
  };

  handleSelectChange = (event: $TSFixMe) => {
    const { executeAction } = this.context;
    const { options } = this.props;
    const selectedId = event.target.value;
    const selectedIndex = options.findIndex((option) => option.id === selectedId);

    executeAction(setSelectedPluginScope, { selectedIndex });
  };

  render() {
    const { options, selectedOption } = this.props;
    const defaultOption = { id: 'default', label: _t('— Use plugin name —'), canRemove: false };
    const optionsWithDefault = options && [defaultOption, ...options];

    return (
      <div>
        <OptionsFormGroup
          label={_t('Plugin')}
          options={optionsWithDefault}
          selectedOption={selectedOption}
          onAddButtonClick={this.handleAddButtonClick}
          onEditButtonClick={this.handleEditButtonClick}
          onRemoveButtonClick={this.handleRemoveButtonClick}
        >
          <select
            className="control"
            value={selectedOption ? selectedOption.id : defaultOption.id}
            onChange={this.handleSelectChange}
          >
            {optionsWithDefault ? (
              optionsWithDefault.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.label}
                </option>
              ))
            ) : (
              <option>{_t('Loading...')}</option>
            )}
          </select>
        </OptionsFormGroup>
      </div>
    );
  }
}

type Stores = {
  WidgetSimulatorStore: typeof WidgetSimulatorStoreClass;
};

export default compose<PropsToComponent, PropsFromCaller>(
  connectToStores<PropsFromStores, {}, Stores>(['WidgetSimulatorStore'], ({ WidgetSimulatorStore }) => ({
    options: WidgetSimulatorStore.getPluginScopes(),
    selectedOption: WidgetSimulatorStore.getSelectedPluginScope(),
  }))
)(PluginScopeEditor);
